import React from "react";

/**
 * Par and stroke index for 18 holes.
 *
 * This is the most tedious form in the app and the one where a mistake
 * does the most damage — a wrong stroke index quietly corrupts every nett
 * score on the course. So:
 *
 *   - the nines sit side by side, halving the scrolling
 *   - par is three buttons, not a field to type in
 *   - stroke index has a "fill it in" button, because 1..18 in order is
 *     right often enough to be worth offering and easy to correct
 *   - duplicates are shown as you type rather than on save
 */

const PARS = [3, 4, 5, 6];

export default function HoleGrid({ tee, onChange }) {
  const counts = {};
  tee.holes.forEach((h) => { counts[h.stroke_index] = (counts[h.stroke_index] || 0) + 1; });
  const dupes = Object.entries(counts).filter(([, n]) => n > 1).map(([si]) => Number(si));
  const totalPar = tee.holes.reduce((a, h) => a + Number(h.par || 0), 0);
  const outPar = tee.holes.slice(0, 9).reduce((a, h) => a + Number(h.par || 0), 0);

  const set = (i, patch) =>
    onChange({ ...tee, holes: tee.holes.map((h, k) => (k === i ? { ...h, ...patch } : h)) });

  const fillIndexes = () =>
    onChange({ ...tee, holes: tee.holes.map((h, i) => ({ ...h, stroke_index: i + 1 })) });

  /** Odds on the front, evens on the back — how most cards are laid out. */
  const splitIndexes = () =>
    onChange({ ...tee, holes: tee.holes.map((h, i) => ({
      ...h, stroke_index: i < 9 ? i * 2 + 1 : (i - 9) * 2 + 2 })) });

  const nine = (from, to, label) => (
    <div className="nine">
      <div className="nine-head">
        <span>{label}</span>
        <span className="nine-par">
          par {tee.holes.slice(from, to).reduce((a, h) => a + Number(h.par || 0), 0)}
        </span>
      </div>
      {tee.holes.slice(from, to).map((h, k) => {
        const i = from + k;
        const dup = dupes.includes(Number(h.stroke_index));
        return (
          <div className="hrow" key={h.hole_number}>
            <span className="hnum">{h.hole_number}</span>
            <span className="hpar">
              {PARS.map((p) => (
                <button type="button" key={p} onClick={() => set(i, { par: p })}
                        className={`parbtn${h.par === p ? " on" : ""}`}>{p}</button>
              ))}
            </span>
            <input className={`hsi${dup ? " dup" : ""}`} inputMode="numeric"
                   value={h.stroke_index}
                   aria-label={`Stroke index for hole ${h.hole_number}`}
                   onChange={(e) => set(i, {
                     stroke_index: Math.max(1, Math.min(18, Number(e.target.value) || 1)) })} />
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="holes">
      <div className="holes-bar">
        <span>
          <b>Par {totalPar}</b>
          <small>{outPar} out · {totalPar - outPar} in</small>
        </span>
        <span className="spacer" />
        <button type="button" className="btn-link" onClick={fillIndexes}>1 to 18 in order</button>
        <button type="button" className="btn-link" onClick={splitIndexes}>Odds out, evens in</button>
      </div>

      <div className="holes-cols">
        {nine(0, 9, "Out")}
        {nine(9, 18, "In")}
      </div>

      {dupes.length > 0 && (
        <div className="notice notice-error" style={{ marginTop: ".6rem" }}>
          Stroke index {dupes.join(" and ")} {dupes.length === 1 ? "is" : "are"} used
          more than once. Every hole needs its own, 1 to 18 — it decides where
          handicap strokes fall.
        </div>
      )}
    </div>
  );
}
